import { useRef, useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import { kecilkan } from '../../../shared/lib/gambar'
import {
  useBuatReferensiPrompt, useDeskripsiReferensi, useJenisReferensi, useNamaReferensi,
  usePensiunReferensi, useReferensi, useReferensiPrompt, useUbahReferensiPrompt,
  useUnggahReferensi,
} from '../../../domains/content/api'
import {
  AreaTeks, Bendera, Body, Gagal, Gambar, Isian, Memuat, Panel, Sub, Tombol,
} from '../../../shared/ui'

const cx = (...a) => a.filter(Boolean).join(' ')

// Sama dengan MAKS_BYTE di `app/web/v1/referensi.py` dan CHECK di migrasi 0029.
// Diperiksa DI SINI supaya berkas kebesaran tidak pernah dikirim: proxy menolaknya
// lebih dulu dengan halaman HTML 413 yang tidak menyebut batas apa pun.
const MAKS_BYTE = 6 * 1024 * 1024

// Sama dengan MAKS_GAMBAR di `ai/prompt_gambar.py`. Yang kelima dulu terpotong DIAM-DIAM
// menurut abjad (XI-5); sekarang kotak centangnya mati dan layarnya menyebut sebabnya.
export const MAKS_DIPAKAI = 4

// Sama dengan MAKS_KEBIJAKAN di `app/web/v1/prompt_template.py`.
const MAKS_PROMPT = 12000

// Sama dengan DeskripsiIn.deskripsi di `app/web/v1/referensi.py`.
const MAKS_UNTUK_APA = 600

// Nama entri yang dibuat kalau tenant belum punya prompt harian sama sekali. Tidak
// ditampilkan di mana pun — layar ini hanya punya satu kotak prompt.
const NAMA_PROMPT_BARU = 'Prompt harian'

// TEMPAT YANG DIISI SISTEM — cermin `TEMPAT` di `ai/prompt_gambar.py`. Tandanya ditulis di
// sini, bukan di kamus: `periksa_isian` menolak `{nama}` tunggal di kamus, dan di sini
// justru itu bentuk yang benar.
export const TEMPAT = [
  ['{topik}', 'tk.phTopik', 'tk.phTopikKet'],
  ['{caption}', 'tk.phCaption', 'tk.phCaptionKet'],
  ['{pesan_utama}', 'tk.phPesan', 'tk.phPesanKet'],
  ['{brief}', 'tk.phBrief', 'tk.phBriefKet'],
  ['{bahan}', 'tk.phBahan', 'tk.phBahanKet'],
  ['{slide_ke}', 'tk.phSlideKe', 'tk.phSlideKeKet'],
  ['{jumlah_slide}', 'tk.phJumlah', 'tk.phJumlahKet'],
]

/** Sisipkan `tanda` di posisi kursor `[awal, akhir)` dari `teks`. MURNI. */
export function sisipkan(teks, tanda, awal, akhir) {
  const t = String(teks ?? '')
  const a = Math.max(0, Math.min(awal ?? t.length, t.length))
  const b = Math.max(a, Math.min(akhir ?? a, t.length))
  return { teks: t.slice(0, a) + tanda + t.slice(b), kursor: a + tanda.length }
}

/** Pecah daftar referensi jadi yang DIPAKAI (dicentang) dan sisanya. MURNI. */
export function pisahkan(daftar) {
  const isi = daftar ?? []
  return {
    dipakai: isi.filter((r) => r?.jenis === 'brand'),
    lain: isi.filter((r) => r?.jenis !== 'brand'),
  }
}

/**
 * REFERENSI — gambar dan prompt untuk AI, SATU halaman (ADR-081).
 *
 * Sampai 22 September dua tab: "Referensi gambar" (bintang brand/sekali pakai/foto orang,
 * keterangan yang tidak sampai ke AI kecuali lewat `{bahan}`) dan "Referensi prompt"
 * (daftar entri berperan, saklar per peran, pemeriksa foto orang). Operator:
 *
 *     "Gabungin referensi gambar dan referensi prompt ... Yang saya inginkan referensi
 *      gambar bisa di pilih. Setiap gambar itu bisa di ketik promptnya bisa untuk apa
 *      gambar itu di gunakan. Lalu Penulisan prompt untuk sehari hari."
 *     "Kita ilangin dulu deh foto orangnya ... Gini aja yang simpel dulu."
 *
 * Jadi alurnya satu garis, dan layarnya mengikuti garis itu dari atas ke bawah:
 *
 *   ① gambar yang DICENTANG + teks "untuk apa"-nya   -> ikut ke penulis DAN model gambar
 *   ② prompt harian                                  -> dibaca penulis, bersama topik & caption
 *
 * DICENTANG = jenis `brand` di database. Tidak ada kolom baru: centang hanyalah nama yang
 * lebih jelas untuk "ikut otomatis", dan jenis lain (`sekali_pakai`, `orang`) sama-sama
 * "tidak dicentang" — masih bisa dipilih tangan di Buat Konten.
 */
export default function Referensi() {
  return (
    <div className="flex flex-col gap-4">
      <BagianGambar />
      <BagianPrompt />
    </div>
  )
}

function BagianGambar() {
  const { t } = useBahasa()
  const daftar = useReferensi()
  const unggah = useUnggahReferensi()
  const pensiun = usePensiunReferensi()
  const jenis = useJenisReferensi()
  const untukApa = useDeskripsiReferensi()
  const namaRef = useNamaReferensi()
  const berkasRef = useRef(null)
  const [galat, setGalat] = useState('')
  const [kabar, setKabar] = useState('')
  // GAMBAR LAIN TERLIPAT. Tenant mengumpulkan puluhan gambar sekali pakai lewat Buat
  // Konten (Sentris: 24), dan menampilkannya semua membuat empat yang dipakai tenggelam.
  const [bukaLain, setBukaLain] = useState(false)

  if (daftar.isLoading) return <Memuat />
  if (daftar.isError) return <Gagal pesan={daftar.error?.message} />

  const { dipakai, lain } = pisahkan(daftar.data)
  const penuh = dipakai.length >= MAKS_DIPAKAI
  const sibuk = jenis.isPending || pensiun.isPending

  const pilih = (r, ya) => jenis.mutate({ id: r.id, jenis: ya ? 'brand' : 'sekali_pakai' })

  const unggahBerkas = async (asli) => {
    setGalat('')
    setKabar('')
    try {
      // BENTUK YANG SAMA dengan PanelGambar. Dua salinan yang menghitung ukuran dengan
      // cara berbeda akan menolak berkas yang sama di satu layar dan menerimanya di lain.
      const { berkas, diubah, sebelum, sesudah } = await kecilkan(asli)
      if (berkas.size > MAKS_BYTE) {
        setGalat(t('bk.refTerlaluBesar', {
          ukuran: (berkas.size / 1024 / 1024).toFixed(1), maks: MAKS_BYTE / 1024 / 1024,
        }))
        return
      }
      if (diubah) {
        setKabar(t('bk.refDikecilkan', {
          lebar: sebelum.lebar, tinggi: sebelum.tinggi,
          lebarBaru: sesudah.lebar, tinggiBaru: sesudah.tinggi,
          dari: (sebelum.bytes / 1024 / 1024).toFixed(1),
          jadi: (sesudah.bytes / 1024).toFixed(0),
        }))
      }
      const baru = await unggah.mutateAsync(berkas)
      // YANG DIUNGGAH DI SINI LANGSUNG DICENTANG selama masih ada tempat: admin yang
      // mengunggah di halaman ini sedang menyiapkan gambar untuk AI, bukan menyimpan arsip.
      if (!penuh && baru?.id) await jenis.mutateAsync({ id: baru.id, jenis: 'brand' })
    } catch (err) {
      setGalat(err?.message || String(err))
    }
  }

  return (
    <Panel
      judul={t('tk.refJudul')}
      catatan={t('tk.refCatatan', { n: dipakai.length, maks: MAKS_DIPAKAI })}
      aksi={(
        <Tombol kecil disabled={unggah.isPending} onClick={() => berkasRef.current?.click()}>
          {unggah.isPending ? t('bk.refMengunggah') : t('tk.refUnggah')}
        </Tombol>
      )}
    >
      <Body className="flex flex-col gap-2.5">
        <input
          ref={berkasRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => {
            const asli = e.target.files?.[0]
            // Dikosongkan supaya memilih berkas YANG SAMA dua kali tetap memicu onChange.
            e.target.value = ''
            if (asli) unggahBerkas(asli)
          }}
        />

        <Sub>{t('tk.refKet')}</Sub>

        {dipakai.length === 0 && (
          <Bendera nada="warn" className="max-w-[76ch]">{t('tk.refBelumDipakai')}</Bendera>
        )}

        {dipakai.map((r) => (
          <BarisDipakai
            key={r.id}
            r={r}
            t={t}
            sibuk={sibuk}
            lepas={() => pilih(r, false)}
            buang={() => pensiun.mutate(r.id)}
            simpanNama={(nama) => namaRef.mutate({ id: r.id, nama })}
            simpanUntukApa={(teks) => untukApa.mutate({ id: r.id, deskripsi: teks })}
          />
        ))}

        {penuh && <Sub>{t('tk.refPenuh', { maks: MAKS_DIPAKAI })}</Sub>}

        {lain.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              aria-expanded={bukaLain}
              onClick={() => setBukaLain(!bukaLain)}
              className="self-start text-[12px] font-semibold text-ink2 hover:text-ink
                         bg-transparent border-0 p-0 cursor-pointer"
            >
              {bukaLain ? '▾' : '▸'} {t('tk.refLain', { n: lain.length })}
            </button>
            {bukaLain && (
              <div className="flex flex-wrap gap-2.5">
                {lain.map((r) => (
                  <label key={r.id}
                         className={cx('flex flex-col gap-1 w-[84px]',
                                       penuh ? 'opacity-60' : 'cursor-pointer')}>
                    <span className="w-[84px] h-[84px] rounded-[5px] border border-line
                                     overflow-hidden bg-surface2 block">
                      <Gambar src={r.url} className="w-full h-full object-cover"
                              teksHilang={t('bk.gambarHilang')} />
                    </span>
                    <span className="flex items-center gap-1">
                      <input type="checkbox" checked={false}
                             disabled={penuh || sibuk}
                             onChange={() => pilih(r, true)}
                             aria-label={t('tk.refPakai', { nama: r.nama })} />
                      <span className="text-[10px] text-ink3 truncate" title={r.nama}>
                        {r.nama}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {(galat || unggah.isError) && (
          <Bendera nada="bad" role="alert">{galat || unggah.error?.message}</Bendera>
        )}
        {(jenis.isError || untukApa.isError || namaRef.isError || pensiun.isError) && (
          <Bendera nada="bad" role="alert">
            {(jenis.error || untukApa.error || namaRef.error || pensiun.error)?.message}
          </Bendera>
        )}
        {kabar && !galat && <Bendera nada="info" role="status">{kabar}</Bendera>}
      </Body>
    </Panel>
  )
}

/**
 * Satu gambar yang DIPAKAI: centang, nama, dan teks "untuk apa".
 *
 * Nama dan teks TERSIMPAN SAAT KURSOR PINDAH, bukan lewat tombol Simpan per baris. Empat
 * baris dengan empat tombol Simpan membuat orang menebak mana yang sudah tersimpan; sekali
 * keluar dari kotak, tersimpan — dan layar menyebutnya sekali di bawah daftar.
 */
export function BarisDipakai({ r, t, sibuk, lepas, buang, simpanNama, simpanUntukApa }) {
  const [teks, setTeks] = useState(r.deskripsi ?? '')
  return (
    <div className="flex gap-3 rounded-[5px] border border-lineStrong bg-surface p-2.5">
      <span className="w-[84px] h-[84px] shrink-0 rounded-[5px] border border-line
                       overflow-hidden bg-surface2 block">
        <Gambar src={r.url} className="w-full h-full object-cover"
                teksHilang={t('bk.gambarHilang')} />
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <span className="flex items-center gap-2">
          <input type="checkbox" checked disabled={sibuk} onChange={lepas}
                 aria-label={t('tk.refLepas', { nama: r.nama })} />
          <Isian
            className="!h-[26px] !text-[12px] font-semibold max-w-[320px]"
            defaultValue={r.nama}
            maxLength={200}
            aria-label={t('tk.refNamaLabel')}
            onBlur={(e) => {
              const nama = e.target.value.trim()
              if (nama && nama !== r.nama) simpanNama(nama)
              else e.target.value = r.nama
            }}
          />
          <span className="flex-1" />
          <button
            type="button"
            title={t('bk.refBuang')}
            aria-label={t('bk.refBuang')}
            disabled={sibuk}
            onClick={buang}
            className="text-[13px] leading-none text-ink3 hover:text-bad bg-transparent
                       border-0 cursor-pointer p-0 shrink-0"
          >
            ×
          </button>
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-ink3">
          {t('tk.refUntukApa')}
        </span>
        <AreaTeks
          baris={2}
          maxLength={MAKS_UNTUK_APA}
          value={teks}
          placeholder={t('tk.refUntukApaContoh')}
          onChange={(e) => setTeks(e.target.value)}
          onBlur={() => {
            if (teks.trim() !== (r.deskripsi ?? '').trim()) simpanUntukApa(teks.trim())
          }}
        />
      </div>
    </div>
  )
}

function BagianPrompt() {
  const daftar = useReferensiPrompt()
  if (daftar.isLoading) return <Memuat />
  if (daftar.isError) return <Gagal pesan={daftar.error?.message} />
  // SATU KOTAK: entri berperan penulis yang nyala. Entri lain (versi lama, pemeriksa foto
  // orang) tetap di database dan tidak tampil — keputusan operator "yang simpel dulu".
  const aktif = (daftar.data ?? []).find((m) => m.peran === 'penulis_prompt' && m.aktif) ?? null
  // `key` = id entrinya: kotak mulai dari isi yang tersimpan, dan mulai ulang kalau entrinya
  // berganti — bukan disalin lewat efek yang bisa menimpa yang sedang diketik.
  return <FormPromptHarian key={aktif?.id ?? 'baru'} aktif={aktif} />
}

/**
 * PROMPT HARIAN — satu kotak, ditulis sekali, dipakai setiap konten otomatis.
 *
 * Tombol tempat isian MENYISIPKAN DI POSISI KURSOR, bukan di ujung: prompt harian Sentris
 * membungkus topik dengan `<topik>…</topik>`, dan tanda yang selalu jatuh di ujung harus
 * dipindahkan tangan tiap kali.
 */
export function FormPromptHarian({ aktif }) {
  const { t } = useBahasa()
  const buat = useBuatReferensiPrompt()
  const ubah = useUbahReferensiPrompt()
  const kotak = useRef(null)
  const semula = aktif?.kebijakan_referensi ?? ''
  const [teks, setTeks] = useState(semula)
  const sedang = buat.isPending || ubah.isPending
  const berubah = teks.trim() !== semula.trim()
  const galat = buat.error?.message || ubah.error?.message

  const sisip = (tanda) => {
    const el = kotak.current
    const hasil = sisipkan(teks, tanda, el?.selectionStart, el?.selectionEnd)
    setTeks(hasil.teks)
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(hasil.kursor, hasil.kursor)
    })
  }

  const simpan = () => {
    const badan = { kebijakan_referensi: teks.trim(), aktif: true, peran: 'penulis_prompt' }
    if (aktif) ubah.mutate({ id: aktif.id, nama: aktif.nama, ...badan })
    else buat.mutate({ nama: NAMA_PROMPT_BARU, ...badan })
  }

  return (
    <Panel judul={t('tk.phJudul')} catatan={t('tk.phCatatan')}>
      <Body className="flex flex-col gap-2.5">
        {!semula.trim() && (
          <Bendera nada="warn" className="max-w-[76ch]">{t('tk.phKosong')}</Bendera>
        )}
        <AreaTeks
          ref={kotak}
          baris={16}
          maxLength={MAKS_PROMPT}
          value={teks}
          placeholder={t('tk.phContoh')}
          onChange={(e) => setTeks(e.target.value)}
          className="font-mono !text-[12px]"
        />
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-ink3 mr-1">
            {t('tk.phSisipkan')}
          </span>
          {TEMPAT.map(([tanda, label, ket]) => (
            <Tombol key={tanda} kecil jenis="sekunder" title={`${tanda} — ${t(ket)}`}
                    onClick={() => sisip(tanda)}>
              {t(label)}
            </Tombol>
          ))}
        </span>
        <Sub>{t('tk.phTempel')}</Sub>
        <span className="flex items-center gap-2">
          <Tombol disabled={sedang || !berubah || !teks.trim()} onClick={simpan}>
            {sedang ? t('umum.menyimpan') : t('umum.simpan')}
          </Tombol>
          {berubah && (
            <Tombol jenis="sekunder" disabled={sedang} onClick={() => setTeks(semula)}>
              {t('umum.batal')}
            </Tombol>
          )}
        </span>
        {galat && <Bendera nada="bad" role="alert">{galat}</Bendera>}
      </Body>
    </Panel>
  )
}
